/* eslint-disable */
import md5 from './md5.js';

/**
 * Base64 encode for browser environment
 * @param {string} str String to encode
 * @returns {string} Base64 encoded string
 */
function base64Encode(str) {
  // replace base64Encode at Miniprogram to btoa at browser
  return window.btoa(str);
}

/**
 * Generate device fingerprint from browser information
 * @returns {string} Base64 encoded fingerprint
 */
function generateFingerprint() {
  // replace wx.getSystemInfoSync() to browser API
  const screen = window.screen;
  const navigator = window.navigator;

  // keep the same format as Miniprogram: model:screenWidth:screenHeight:SDKVersion
  const deviceInfo = `${navigator.userAgent.substring(0, 50)}:${screen.width}:${
    screen.height
  }:${navigator.language}`;
  return base64Encode(deviceInfo);
}

/**
 * Generate random nonce
 * @returns {string} Random nonce string
 */
function generateNonce() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

/**
 * Generate UTC timestamp synchronized with server time
 * @param {number} serverTimeOffset Server time offset in milliseconds
 * @returns {string} Current timestamp as string
 */
function generateTimestamp(serverTimeOffset = 0) {
  const adjustedTime = Date.now() + serverTimeOffset;
  return adjustedTime.toString();
}

/**
 * Generate complete signature headers
 * @param {string} secret Signature secret key (replace Miniprogram's getServerConfig)
 * @param {number} serverTimeOffset Server time offset in milliseconds
 * @returns {Promise<Object>} Object containing all signature headers
 */
async function generateSignatureHeaders(secret, serverTimeOffset = 0) {
  try {
    const fingerprint = generateFingerprint();
    const nonce = generateNonce();
    const timestamp = generateTimestamp(serverTimeOffset);

    if (!secret) {
      throw new Error('Signature secret not configured');
    }

    // Generate signature: MD5(fingerprint + nonce + timestamp + secret)
    const signatureData = `${fingerprint}${nonce}${timestamp}${secret}`;
    const signature = md5(signatureData);

    return {
      'x-sign-fingerprint': fingerprint,
      'x-sign-nonce': nonce,
      'x-sign-timestamp': timestamp,
      'x-sign-signature': signature,
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Check if URL needs signature
 * @param {string} url Request URL
 * @param {Array} requiredUrls URLs that need signature validation
 * @returns {boolean} Whether the URL needs signature
 */
function needsSignature(url, requiredUrls = []) {
  return requiredUrls.some((pattern) => url.includes(pattern));
}

/**
 * inject signature headers to request
 * @param {string} url Request URL
 * @param {string} secret Signature secret key
 * @param {number} serverTimeOffset Server time offset in milliseconds
 * @param {Array} requiredUrls URLs that need signature validation
 * @returns {Promise<Object>} Signature headers or empty object
 */
async function injectSignatureHeaders(
  url,
  secret,
  serverTimeOffset = 0,
  requiredUrls = [],
) {
  if (!needsSignature(url, requiredUrls)) {
    return {};
  }

  return await generateSignatureHeaders(secret, serverTimeOffset);
}

export { injectSignatureHeaders };
